import { Injectable } from "@angular/core";
import moment from "moment";
import { AuthService } from "../../app/services/auth.service";
import { SubscriptionDateService } from "./subscription-date.service";
import { CacheManagementService } from "./cache-management.service";
import { ApiService } from "../provider/api.service";
import { endpoints } from "src/app/constant/endpoints";
import {
  catchError,
  first,
  map,
  switchMap,
  filter,
  shareReplay,
  distinctUntilChanged,
  tap,
  timeout,
  debounceTime,
  retry,
} from "rxjs/operators";
import {
  Observable,
  of,
  throwError,
  BehaviorSubject,
  forkJoin,
  Subject,
  timer,
  combineLatest,
} from "rxjs";
import { defer, EMPTY } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class DataService {
  customer?: any | null;
  authToken?: string | null;
  today: string = moment().format("YYYY-MM-DD");

  private _currentUser$ = new BehaviorSubject<any | null>(null);

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private subscriptionDateService: SubscriptionDateService,
    private cacheService: CacheManagementService,
  ) {
    // Auth state is managed by App Component
    this.authService.currentUser$.subscribe((user) => {
      this.customer = user;
      this.authToken = user?.session_token || null;
      this._currentUser$.next(user);
    });
  }

  private fetchData<T>(url: string): Observable<T> {
    // Check cache first
    const cached = this.cacheService.get<T>(url, "data-service");
    if (cached !== null) {
      console.log(`[DataService] Using cached data for ${url}`);
      return of(cached);
    }

    // Not cached, make API call
    console.log(`[DataService] Fetching from API: ${url}`);
    return this.apiService?.get(url).pipe(
      map((response) => {
        if (response?.data != null) {
          return response.data as T;
        }
        throw new Error(`API returned no data for ${url}`);
      }),
      tap((data) => {
        // Store in CacheManagementService with TTL
        this.cacheService.set(url, data, "data-service");
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error) => {
        return throwError(
          () => new Error(`Failed to fetch data from ${url}: ${error.message}`),
        );
      }),
    );
  }

  private loadSliderData(): Observable<any[]> {
    // We now have to cast to the correct type after fetching
    return this.fetchData<any[]>(endpoints.slider);
  }
  private loadCMSPagesData(): Observable<any[]> {
    // We now have to cast to the correct type after fetching
    return this.fetchData<any[]>(endpoints.cms);
  }
  private loadConfigurationsData(): Observable<any> {
    // This correctly fetches an object, not an array
    return this.fetchData<any>(endpoints.configuration);
  }

  private loadProfileData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any>(`${endpoints.profile}?customer_id=${user.id}`),
      ),
    );
  }

  private loadSubscriptionsData(): Observable<any[]> {
    return this.fetchData<any[]>(endpoints.subscriptions);
  }

  /**
   * Loads subscription data from SubscriptionDateService (single source of truth)
   * This method now delegates to SubscriptionDateService instead of making direct API calls
   * to eliminate duplicate subscription fetching across the app.
   */
  private loadMySubscriptionsNewData(): Observable<any> {
    console.log(
      "[DataService] Loading subscriptions from SubscriptionDateService",
    );

    return this.subscriptionDateService.getSubscriptions$().pipe(
      first(),
      map((subscriptions) => {
        return { subscriptions: subscriptions || [] };
      }),
      catchError((error) => {
        console.error("[DataService] Error loading subscriptions:", error);
        return of({ subscriptions: [] });
      }),
    );
  }

  private loadSubDataData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any>(`${endpoints.subData}?customer_id=${user.id}`),
      ),
    );
  }

  private loadCalendarData(month: string): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => {
        const url = `${endpoints.calendar}?customer_id=${user.id}&month=${month}`;
        return this.fetchData<any>(url);
      }),
    );
  }

  private loadAddressesData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any[]>(`${endpoints.addresses}?customer_id=${user.id}`),
      ),
    );
  }

  private loadSlotData(area_id: number): Observable<any[]> {
    return this.fetchData<any[]>(`${endpoints.slots}?area_id=${area_id}`);
  }

  private loadDriverNoteData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any[]>(
          `${endpoints.driver_notes}?customer_id=${user.id}`,
        ),
      ),
    );
  }

  private loadConsultationsData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any[]>(
          `${endpoints.consultations}?customer_id=${user.id}`,
        ),
      ),
    );
  }
  private loadConsultantsData(date: string): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) =>
        this.fetchData<any[]>(
          `${endpoints.consultants}?customer_id=${user.id}&date=${date}`,
        ),
      ),
    );
  }

  private loadMenuItemsData(): Observable<any> {
    return this.fetchData<any>(endpoints.menuItems);
  }
  private loadAreasData(): Observable<any> {
    return this.fetchData<any>(endpoints.areas);
  }

  private loadMenuPageData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((_user) => {
        const url = `${endpoints.menuSubscriptions}`;
        return this.fetchData<any>(url);
      }),
    );
  }

  private isGuest(): boolean {
  return !this.authService.getAuthUser();
}

  public getMenuSubscriptions(date?: string): Observable<any> {
    const isGuestUser = !this.authService.getAuthUser();
    const today = moment().format("YYYY-MM-DD");

    // if (isGuestUser && date && date !== today) {
    //   console.log("[DataService] Guest user – future date blocked");
    //   return of(null);
    // }

    const url = date
      ? `${endpoints.menuSubscriptions}?date=${moment(date).format("YYYY-MM-DD")}`
      : endpoints.menuSubscriptions;

    return this.fetchData<any>(url);
  }

  private loadMenuWithDateData(date: string): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((_user) => {
        const url = `${endpoints.menuSubscriptions}&date=${date}`;
        return this.fetchData<any>(url);
      }),
    );
  }

  preloadEssentialData(): Observable<any> {
    return defer(() => {
      return forkJoin([
        this.loadConfigurationsData().pipe(
          timeout(15000),
          catchError((err) => {
            console.error(
              "[preloadEssentialData] Configuration load failed:",
              err,
            );
            return of(null);
          }),
        ),
      ]).pipe(
        tap((_data) => {
          console.log("[preloadEssentialData] All data loaded successfully");
        }),
        catchError((err) => {
          console.error("[preloadEssentialData] Error:", err);
          return of(null);
        }),
      );
    });
  }

  newUserLoad(): Observable<any> {
    return defer(() => {
      return forkJoin([
        this.loadSubscriptionsData().pipe(timeout(5000)),
        this.loadMenuItemsData().pipe(timeout(5000)),
        this.loadAreasData().pipe(timeout(5000)),
      ]).pipe(
        tap((_data) => {
          // console.log('[newUserLoad] All data loaded successfully');
        }),
        catchError((err) => {
          console.error("[newUserLoad] Error:", err);
          return of(null);
        }),
      );
    });
  }

  /**
   * Load all user data with proper dependency management
   * IMPORTANT: SubscriptionDateService must be initialized before calling this
   */
  loadUserData(): Observable<any> {
    console.log("[DataService] Loading user data");

    const currentMonth = moment().format("YYYY-MM");

    return defer(() => {
      const user = this._currentUser$.getValue();
      if (!user?.id) {
        console.warn("[DataService] No user for data load");
        return of(null);
      }

      // Load all user data in parallel (SubscriptionDateService already initialized)
      return forkJoin([
        this.loadProfileData(),
        this.loadMySubscriptionsNewData(), // Now uses SubscriptionDateService
        this.loadSubDataData(),
        this.loadCalendarData(currentMonth),
      ]).pipe(
        tap(() => console.log("[DataService] User data loaded successfully")),
        catchError((err) => {
          console.error("[DataService] Error:", err);
          return of(null);
        }),
      );
    });
  }

  backgroundData(): Observable<any> {
    return defer(() => {
      return forkJoin([
        // this.loadAddressesData(),
        // this.loadConsultationsData(),
        // this.loadConsultantsData(this.today),
        // this.loadCMSPagesData(),
        // this.loadDriverNoteData(),
      ]).pipe(
        tap((_data) => {
          // console.log('[backgroundData] Data arrived & emitted');
        }),
        catchError((err) => {
          console.error("[backgroundData] Error:", err);
          return of(null);
        }),
      );
    });
  }

  public getData(
    key: string,
    params?: any,
    forceRefresh: boolean = false,
  ): Observable<any> {
    if (forceRefresh) {
      const url = this.getUrlForKey(key, params);
      // Clear from CacheManagementService
      this.cacheService.delete(url, "data-service");
    }

    switch (key) {
      case "slider":
        return this.loadSliderData();
      case "areas":
        return this.loadAreasData();
      case "pages":
        return this.loadCMSPagesData();
      case "configurations":
        return this.loadConfigurationsData();
      case "profile":
        return this.loadProfileData();
      case "subscriptions":
        return this.loadSubscriptionsData();
      case "mysubscriptionsnew":
        return this.loadMySubscriptionsNewData();
      case "subdata":
        return this.loadSubDataData();
      case "calendar":
        const month = params?.month || moment().format("YYYY-MM");
        return this.loadCalendarData(month);
      case "addresses":
        return this.loadAddressesData();
      case "menuItems":
        return this.loadMenuItemsData();
      case "slots":
        const areaId = params?.area_id || 1;
        return this.loadSlotData(areaId);
      case "driverNote":
        return this.loadDriverNoteData();
      case "consultations":
        return this.loadConsultationsData();
      case "consultants":
        const bookDate = params?.date || this.today;
        return this.loadConsultantsData(bookDate);
      case "menuList":
        return this.loadMenuPageData();
      case "menuWithDate":
        const date = params?.date;
        return this.loadMenuWithDateData(date);
      default:
        return of(null);
    }
  }

  private getUrlForKey(key: string, params?: any): string | null {
    const user = this._currentUser$.getValue();
    const customerId = user?.id;
    const areaId = params?.area_id || 1;
    const month = params?.month || moment(this.today).format("YYYY-MM");
    const date = params?.date || this.today;
    const doubleBox = params?.doubleBox || false;
    switch (key) {
      case "slider":
        return endpoints.slider;
      case "areas":
        return endpoints.areas;
      case "configurations":
        return endpoints.configuration;
      case "profile":
        return customerId
          ? `${endpoints.profile}?customer_id=${customerId}`
          : null;
      case "subscriptions":
        return endpoints.subscriptions;
      case "mysubscriptionsnew":
        return customerId
          ? `${endpoints.mySubscriptionsnew}?customer_id=${customerId}`
          : null;
      case "subdata":
        return customerId
          ? `${endpoints.subData}?customer_id=${customerId}`
          : null;
      case "calendar":
        return customerId
          ? `${endpoints.calendar}?customer_id=${customerId}&month=${month}`
          : null;
      case "addresses":
        return customerId
          ? `${endpoints.addresses}?customer_id=${customerId}`
          : null;
      case "menuItems":
        return endpoints.menuItems;
      case "slots":
        return `${endpoints.slots}?area_id=${areaId}`;
      case "driverNote":
        return customerId
          ? `${endpoints.driver_notes}?customer_id=${customerId}`
          : null;
      case "consultations":
        return customerId
          ? `${endpoints.consultations}?customer_id=${customerId}`
          : null;
      case "consultants":
        return customerId
          ? `${endpoints.consultants}?customer_id=${customerId}&date=${date}`
          : null;
      case "menuWithDate":
        return customerId && date
          ? `${endpoints.menus}?customer_id=${customerId}&date=${date}&double_box=${doubleBox}`
          : null;
      case "menuList":
        return customerId && date
          ? `${endpoints.menus}?customer_id=${customerId}&date=${date}&double_box=${doubleBox}`
          : null;
      default:
        return null;
    }
  }

  clearUserSpecificData(): void {
    // Clear user-specific data from cache
    this.cacheService.clear("data-service");
  }

  showData() {
    // Return all cached data from CacheManagementService
    const stats = this.cacheService.getStats("data-service");
    console.log("[DataService] Cache stats:", stats);
    // Note: CacheManagementService doesn't expose all entries directly
    // This method is primarily for debugging
    return { stats, message: "Use getData() to fetch specific data" };
  }

  getCachedData(key: string, params?: any, force: boolean = false): any | null {
    const url = this.getUrlForKey(key, params);
    if (!url) {
      return null;
    }

    // Try to get from CacheManagementService
    const cached = this.cacheService.get(url, "data-service");

    if (cached == null || force) {
      // Trigger async fetch if not cached or force refresh
      this.getData(key, params, true).subscribe();
      return null;
    }

    return cached;
  }

  clearAllData() {
    this.cacheService.clear("data-service");
  }
}

/*
// Basic Observable Call - 

this.dataService.getData('pages').subscribe(res => {
  // console.log('async data', res);
});

// sync access (after first load)
const pagesData = this.dataService.getCachedData('pages');
// console.log('sync data', pagesData);

// all cached at once
const allCached = this.dataService.showData();
// console.log(allCached);

this.dataService.getData('profile').subscribe(data => {
  // console.log('Raw API data', data);
});

// Save Transformed Version - 

this.dataService.getData('profile').subscribe(raw => {
  const transformed = { ...raw, fullName: raw.first_name + ' ' + raw.last_name };
  this.dataService.setTransformedData('profile_transformed', transformed);
});

// Access saved transformed version from anywhere - 

this.dataService.getTransformedData('profile_transformed').subscribe(transformed => {
  // console.log('Transformed profile', transformed);
});

// Syncronized Data - if sure data exists can use this - 

const cached = this.dataService.getTransformedSnapshot('profile_transformed');
// console.log(cached);
*/
